// ShapChart — SHAP Top-K 贡献特征图
// 规格: SPEC §6 ui-kit/explainability / ADR-0013 (ECharts 5.5 按需引入) / ADR-0008 (SaMD 路径 A)
//
// SaMD 路径 A 约束 (ADR-0008):
//   仅展示客观数据与 AI 可解释性, 禁止诊断结论文案 (MED-02)
//   本组件展示 SHAP 特征贡献度, 不输出任何诊断结论

import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts/core';
import type { ComposeOption } from 'echarts/core';
import { BarChart } from 'echarts/charts';
import type { BarSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
} from 'echarts/components';
import type {
  GridComponentOption,
  TooltipComponentOption,
  TitleComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// ECharts 按需注册 (ADR-0013)
echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  CanvasRenderer,
]);

type ShapOption = ComposeOption<
  | BarSeriesOption
  | GridComponentOption
  | TooltipComponentOption
  | TitleComponentOption
>;

/** SaMD 路径 A 颜色: 客观展示贡献方向 (非诊断色) */
const COLOR_POSITIVE = '#d64545'; // 增加风险 (红)
const COLOR_NEGATIVE = '#3e9d6e'; // 降低风险 (绿)

const DEFAULT_TOP_K = 10;

/** 单个 SHAP 特征贡献 */
export interface ShapFeature {
  /** 特征名称 */
  readonly name: string;
  /** SHAP 值 (有符号) */
  readonly value: number;
  /** 贡献方向: positive 增加风险 / negative 降低风险 */
  readonly direction: 'positive' | 'negative';
}

export interface ShapChartProps {
  /** 特征贡献数组 */
  readonly features: readonly ShapFeature[];
  /** 展示前 K 项 (默认 10) */
  readonly topK?: number;
  /** 容器高度 (默认 320px) */
  readonly height?: number | string;
  /** 自定义无障碍标签 */
  readonly ariaLabel?: string;
}

/**
 * ShapChart — SHAP Top-K 贡献特征图
 *
 * 规格:
 *   - SPEC §6 ui-kit/explainability
 *   - ADR-0013: ECharts 5.5.x 按需引入 (BarChart + GridComponent)
 *   - ADR-0008 SaMD 路径 A: 仅展示客观数据与 AI 可解释性, 禁止诊断结论文案
 *
 * 渲染:
 *   - ECharts BarChart 横向条形图
 *   - 正向贡献 (增加风险) → 红色
 *   - 负向贡献 (降低风险) → 绿色
 *   - 按 |SHAP value| 降序取 Top-K
 */
export function ShapChart({
  features,
  topK = DEFAULT_TOP_K,
  height = 320,
  ariaLabel,
}: ShapChartProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  // Top-K 按 |value| 降序; 使用 inverse: true 使最大值显示在顶部
  const top = useMemo(() => {
    const sorted = [...features].sort(
      (a, b) => Math.abs(b.value) - Math.abs(a.value),
    );
    return sorted.slice(0, Math.max(0, topK));
  }, [features, topK]);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;
    return () => {
      // StrictMode 双挂载靠 dispose 幂等 (ADR-0013)
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const option: ShapOption = {
      title: {
        text: '特征贡献度 (SHAP)',
        left: 'center',
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'SHAP 值',
      },
      yAxis: {
        type: 'category',
        data: top.map((f) => f.name),
        inverse: true, // Top-1 显示在顶部
      },
      series: [
        {
          type: 'bar',
          data: top.map((f) => ({
            value: f.value,
            itemStyle: {
              color:
                f.direction === 'positive'
                  ? COLOR_POSITIVE
                  : COLOR_NEGATIVE,
            },
          })),
        },
      ],
    };

    // 增量更新, 避免全量 diff (ADR-0013 ops-admin 实时流式场景)
    chart.setOption(option, { notMerge: false });
  }, [top]);

  const summary =
    top.length > 0
      ? `Top-${top.length} 特征贡献度 (共 ${features.length} 项)`
      : '暂无特征贡献数据';

  return (
    <div
      role="img"
      aria-label={ariaLabel ?? `SHAP 特征贡献图: ${summary}`}
      style={{ width: '100%', height }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {/* WCAG 2.1 SC 1.1.1: 屏幕阅读器数据表格替代 (Canvas 对 AT 不可见) */}
      <table className="sr-only">
        <caption>SHAP 特征贡献度数据 (客观展示, 非诊断结论)</caption>
        <thead>
          <tr>
            <th scope="col">特征名称</th>
            <th scope="col">SHAP 值</th>
            <th scope="col">贡献方向</th>
          </tr>
        </thead>
        <tbody>
          {top.map((f) => (
            <tr key={f.name}>
              <th scope="row">{f.name}</th>
              <td>{f.value}</td>
              <td>
                {f.direction === 'positive' ? '增加风险' : '降低风险'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
